'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { createClient } from '@/lib/supabase/client';
import { formatNPR } from '@/lib/i18n';
import LocationBrowser, { LocationSelection, formatLocationSelection } from '@/components/LocationBrowser';
import InteractiveMap, { MapMarker } from '@/components/InteractiveMap';

interface Hotel {
  id: string;
  name: string;
  location: string;
  star_rating: number;
  customer_rating: number;
  reviews_count: number;
  price_per_night: number;
  is_featured: boolean;
  image_url?: string;
  amenities?: string[];
  description?: string;
}

export default function HotelsPage() {
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [starFilter, setStarFilter] = useState('all');
  const [locationSel, setLocationSel] = useState<LocationSelection>({});
  const [showMap, setShowMap] = useState(false);

  useEffect(() => {
    const fetchHotels = async () => {
      setLoading(true);
      const supabase = createClient();
      const { data } = await supabase.from('hotels').select('*').order('is_featured', { ascending: false }).order('customer_rating', { ascending: false });
      if (data) {
        setHotels(data);
      }
      setLoading(false);
    };
    fetchHotels();
  }, []);

  const locationLabel = formatLocationSelection(locationSel);

  const filtered = hotels.filter(h => {
    const matchSearch = !search || h.name?.toLowerCase().includes(search.toLowerCase()) || h.location?.toLowerCase().includes(search.toLowerCase());
    const matchStar = starFilter === 'all' || String(h.star_rating) === starFilter;
    // Location filter: match city or district name against hotel location string
    const matchLocation = !locationSel.city && !locationSel.district && !locationSel.province
      ? true
      : (locationSel.city ? h.location?.toLowerCase().includes(locationSel.city.name.toLowerCase().split(' ')[0]) :
         locationSel.district ? h.location?.toLowerCase().includes(locationSel.district.name.toLowerCase()) :
         locationSel.province ? h.location?.toLowerCase().includes(locationSel.province.name.toLowerCase()) : true);
    return matchSearch && matchLocation && matchStar;
  });

  const hotelCoords: Record<string, { lat: number; lng: number }> = {
    'Kathmandu': { lat: 27.7172, lng: 85.3240 },
    'Pokhara': { lat: 28.2096, lng: 83.9856 },
    'Chitwan': { lat: 27.5291, lng: 84.3542 },
    'Biratnagar': { lat: 26.4525, lng: 87.2718 },
    'Butwal': { lat: 27.7006, lng: 83.4483 },
    'Mustang': { lat: 28.9965, lng: 83.8500 },
  };

  const mapMarkers: MapMarker[] = filtered.map(h => {
    const loc = Object.entries(hotelCoords).find(([k]) => h.location?.toLowerCase().includes(k.toLowerCase()));
    const coords = loc ? loc[1] : { lat: 27.7172 + (Math.random() - 0.5) * 0.5, lng: 85.3240 + (Math.random() - 0.5) * 0.5 };
    return {
      id: h.id,
      lat: coords.lat,
      lng: coords.lng,
      title: h.name,
      description: `📍 ${h.location} · ⭐ ${h.customer_rating || '—'}`,
      category: 'hotel',
      price: h.price_per_night ? `NPR ${h.price_per_night.toLocaleString()}/night` : undefined,
      rating: h.customer_rating,
      image: h.image_url,
      link: `/hotels/${h.id}`,
    };
  });

  return (
    <main className="min-h-screen bg-navy">
      <Header />
      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 mb-8">
          <div className="text-center mb-8">
            <h1 className="text-snow font-bold text-3xl md:text-4xl mb-3">Hotels in Nepal</h1>
            <p className="text-snow/60 text-lg max-w-2xl mx-auto">From luxury mountain lodges to budget guesthouses — find your perfect stay.</p>
          </div>

          {/* Location Browser */}
          <div className="yn-card rounded-2xl p-4 mb-4 max-w-4xl mx-auto">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-gold text-sm">📍</span>
              <h3 className="text-snow text-sm font-semibold">Filter by Location</h3>
              {locationLabel && (
                <span className="ml-auto text-xs text-gold/80 bg-gold/10 px-2 py-0.5 rounded-full truncate max-w-xs">{locationLabel}</span>
              )}
              {locationLabel && (
                <button onClick={() => setLocationSel({})} className="text-snow/40 hover:text-snow/70 text-xs transition-colors ml-1">✕ Clear</button>
              )}
            </div>
            <LocationBrowser
              nepalOnly
              levels={['province', 'district', 'city']}
              value={locationSel}
              onChange={setLocationSel}
              compact
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-3 max-w-3xl mx-auto">
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search hotels..."
              className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-snow placeholder-snow/30 focus:outline-none focus:border-gold/40" />
            <select value={starFilter} onChange={e => setStarFilter(e.target.value)}
              className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-snow focus:outline-none focus:border-gold/40">
              <option value="all">All Stars</option>
              {[5,4,3,2,1].map(s => <option key={s} value={String(s)}>{s} Star</option>)}
            </select>
            <button
              onClick={() => setShowMap(m => !m)}
              className={`flex items-center gap-2 px-5 py-3 rounded-xl border text-sm font-medium transition-all whitespace-nowrap ${showMap ? 'bg-gold/20 border-gold/50 text-gold' : 'bg-white/5 border-white/10 text-snow/70 hover:text-snow hover:border-gold/30'}`}
            >
              🗺️ {showMap ? 'Hide Map' : 'Map View'}
            </button>
          </div>
        </div>

        {/* Interactive Map */}
        {showMap && (
          <div className="max-w-7xl mx-auto px-4 mb-8">
            <div className="yn-card rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-gold text-lg">🗺️</span>
                <h2 className="text-snow font-bold text-lg">Hotels Map</h2>
                <span className="text-snow/40 text-xs ml-auto">Satellite · Traffic · Route Planning · Nearby Search</span>
              </div>
              <InteractiveMap
                markers={mapMarkers}
                centerLat={27.7172}
                centerLng={85.3240}
                zoom={8}
                height="460px"
                showControls
                showNearbySearch
                showRoutePlanning
              />
            </div>
          </div>
        )}

        <div className="max-w-7xl mx-auto px-4">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[1,2,3,4,5,6].map(i => <div key={i} className="h-72 bg-white/5 rounded-2xl animate-pulse" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-4xl mb-3">🏨</div>
              <h3 className="text-snow font-semibold mb-2">No hotels found</h3>
              <p className="text-snow/40 text-sm">Try adjusting your search or location filters</p>
              <button onClick={() => { setSearch(''); setStarFilter('all'); setLocationSel({}); }}
                className="mt-4 text-gold text-sm hover:underline">Clear all filters</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map(hotel => (
                <div key={hotel.id} className="yn-card rounded-2xl overflow-hidden hover:border-gold/30 transition-all group">
                  <div className="h-44 bg-gradient-to-br from-navy-mid to-navy relative overflow-hidden">
                    {hotel.image_url ? (
                      <img src={hotel.image_url} alt={`${hotel.name} hotel in ${hotel.location}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><span className="text-5xl opacity-30">🏨</span></div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-navy/70 to-transparent" />
                    {hotel.is_featured && <div className="absolute top-3 right-3 bg-gold text-navy text-[10px] font-bold px-2 py-0.5 rounded-full">⭐ Featured</div>}
                    <div className="absolute bottom-3 left-3">
                      <div className="text-gold text-sm">{'⭐'.repeat(hotel.star_rating || 3)}</div>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="text-snow font-bold text-base mb-1">{hotel.name}</h3>
                    <p className="text-snow/50 text-xs mb-2">📍 {hotel.location}</p>
                    {hotel.description && <p className="text-snow/60 text-xs line-clamp-2 mb-3">{hotel.description}</p>}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-1">
                        <span className="text-gold text-sm">⭐ {hotel.customer_rating || '—'}</span>
                        <span className="text-snow/40 text-xs">({hotel.reviews_count || 0} reviews)</span>
                      </div>
                      {hotel.price_per_night && (
                        <div className="text-right">
                          <div className="text-gold font-bold text-sm">{formatNPR(hotel.price_per_night)}</div>
                          <div className="text-snow/40 text-[10px]">per night</div>
                        </div>
                      )}
                    </div>
                    {hotel.amenities && hotel.amenities.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {hotel.amenities.slice(0, 3).map((a: string) => (
                          <span key={a} className="text-[10px] px-2 py-0.5 bg-white/5 text-snow/50 rounded-full">{a}</span>
                        ))}
                      </div>
                    )}
                    <Link href={`/hotels/${hotel.id}`}
                      className="block w-full text-center py-2 bg-gold/20 text-gold border border-gold/30 rounded-xl text-sm font-medium hover:bg-gold/30 transition-colors">
                      Book Now
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </main>
  );
}
